import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ChatCircle, PaintBrush, GameController, Pencil } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { ThoughtCard } from '../components/ThoughtCard';
import { GamesArcade } from '../components/games/GamesArcade';
import { WelcomeBackButton } from '../components/WelcomeBackButton';
import { toast } from 'sonner';
import axios from 'axios';
import CanvasDraw from 'react-canvas-draw';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function MembersPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [thoughts, setThoughts] = useState([]);
  const [newThought, setNewThought] = useState({ title: '', content: '', is_anonymous: false });
  const [canvasRef, setCanvasRef] = useState(null);
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 400 });
  const activeTab = searchParams.get('tab') || 'thoughts';

  useEffect(() => {
    const updateCanvasSize = () => {
      const containerWidth = Math.min(window.innerWidth - 64, 800);
      setCanvasSize({
        width: containerWidth,
        height: Math.min(containerWidth * 0.55, 400)
      });
    };
    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    return () => window.removeEventListener('resize', updateCanvasSize);
  }, []);

  useEffect(() => {
    fetchThoughts();
  }, []);

  const fetchThoughts = async () => {
    try {
      const { data } = await axios.get(`${API}/thoughts`, { withCredentials: true });
      setThoughts(data);
    } catch (error) {
      console.error('Failed to fetch thoughts:', error);
    }
  };

  const submitThought = async () => {
    if (!newThought.title || !newThought.content) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      await axios.post(`${API}/thoughts`, newThought, { withCredentials: true });
      toast.success('Thought shared successfully!');
      setNewThought({ title: '', content: '', is_anonymous: false });
      fetchThoughts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to share thought');
    }
  };

  const clearCanvas = () => {
    if (canvasRef) {
      canvasRef.clear();
    }
  };

  return (
    <div className="min-h-screen py-8 md:py-12 px-4 sm:px-6" data-testid="members-page">
      <div className="w-full max-w-[1600px] mx-auto">
        <div className="mb-6 md:mb-8">
          <h1 className="text-3xl sm:text-4xl tracking-tight font-light mb-2" data-testid="members-heading">
            Welcome, <span className="text-primary">{user?.username}</span>
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Membership: <span className="text-white" data-testid="user-membership-level">
              {user?.membership_level === 0 ? 'Free' : `Level ${user?.membership_level}`}
            </span> | 
            Chat Credits: <span className="text-white" data-testid="user-chat-credits">{user?.chat_credits || 0}</span>
          </p>
        </div>

        <div className="mb-8 md:mb-10 flex justify-center">
          <WelcomeBackButton onPress={() => setSearchParams({ tab: 'games' })} />
        </div>

        <Tabs value={activeTab} onValueChange={(value) => setSearchParams({ tab: value })} className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-6 md:mb-8">
            <TabsTrigger value="thoughts" className="text-xs sm:text-sm" data-testid="tab-thoughts">
              <Pencil size={14} className="mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Thoughts</span>
              <span className="sm:hidden">Post</span>
            </TabsTrigger>
            <TabsTrigger value="draw" className="text-xs sm:text-sm" data-testid="tab-draw">
              <PaintBrush size={14} className="mr-1 sm:mr-2" />
              Draw
            </TabsTrigger>
            <TabsTrigger value="games" className="text-xs sm:text-sm" data-testid="tab-games">
              <GameController size={14} className="mr-1 sm:mr-2" />
              Games
            </TabsTrigger>
            <TabsTrigger value="anonymous" className="text-xs sm:text-sm" data-testid="tab-anonymous">
              <ChatCircle size={14} className="mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Connect</span>
              <span className="sm:hidden">Chat</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="thoughts" className="space-y-6">
            <Card className="p-6" data-testid="thoughts-create-section">
              <h3 className="text-2xl tracking-tight font-light mb-4">Share Your Thoughts</h3>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    placeholder="What's on your mind?"
                    value={newThought.title}
                    onChange={(e) => setNewThought({ ...newThought, title: e.target.value })}
                    data-testid="thought-title-input"
                  />
                </div>
                <div>
                  <Label htmlFor="content">Content</Label>
                  <Textarea
                    id="content"
                    placeholder="Express yourself..."
                    rows={4}
                    value={newThought.content}
                    onChange={(e) => setNewThought({ ...newThought, content: e.target.value })}
                    data-testid="thought-content-input"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="anonymous"
                    checked={newThought.is_anonymous}
                    onChange={(e) => setNewThought({ ...newThought, is_anonymous: e.target.checked })}
                    className="rounded"
                    data-testid="thought-anonymous-checkbox"
                  />
                  <Label htmlFor="anonymous" className="font-normal cursor-pointer">Post anonymously</Label>
                </div>
                <Button onClick={submitThought} className="rounded-full" data-testid="thought-submit-button">
                  Share Thought
                </Button>
              </div>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {thoughts.map((thought) => (
                <ThoughtCard key={thought.id} thought={thought} onUpdate={fetchThoughts} />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="draw">
            <Card className="p-4 sm:p-6" data-testid="draw-section">
              <h3 className="text-2xl tracking-tight font-light mb-4">Drawing Board</h3>
              <div className="bg-white rounded overflow-hidden">
                <CanvasDraw
                  ref={(ref) => setCanvasRef(ref)}
                  canvasWidth={canvasSize.width}
                  canvasHeight={canvasSize.height}
                  brushRadius={2}
                  brushColor="#000000"
                  data-testid="canvas-draw"
                />
              </div>
              <Button onClick={clearCanvas} className="mt-4 rounded-full" data-testid="canvas-clear-button">
                Clear Canvas
              </Button>
            </Card>
          </TabsContent>

          <TabsContent value="games">
            <div className="mb-6" data-testid="games-section">
              <h3 className="text-2xl tracking-tight font-light mb-1">Games Arcade</h3>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Play solo, take on our AI opponents, or let an AI host run a word game. Every score counts toward the leaderboards.
              </p>
            </div>
            <GamesArcade />
          </TabsContent>

          <TabsContent value="anonymous">
            <Card className="p-6" data-testid="anonymous-connect-section">
              <h3 className="text-2xl tracking-tight font-light mb-4">Connect Anonymously</h3>
              <p className="text-sm text-muted-foreground mb-6">
                Join chat rooms to connect with others anonymously. Go to the Chat Rooms page to start chatting!
              </p>
              <Button onClick={() => window.location.href = '/chat-rooms'} className="rounded-full" data-testid="goto-chatrooms-button">
                Go to Chat Rooms
              </Button>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}